/**
 * Ready Module ASR-check ("mic check") stories. StudentASRChecks runs them through
 * ReaderManager.launchSelfAssignedActivity as ordinary `tutor` activities, so nothing but the
 * storyId tells an ASR-check row apart from a real story in SRS. A completed check is marked
 * `archived` (ReaderManager.onStoryEnd), but a check the student walked away from stays at
 * `not_started` — roughly one mic-check row in seven on prod2 as of 2026-09 — so any tutor-history read that
 * wants "the last real story" has to exclude by storyId, not by status.
 *
 * SRS returns storyId as an upper-case, dash-less hex string (hex(activity.storyId)), which is
 * what ASR_CHECK_STORY_ID_HEX holds; the UUID forms are what getStory and the router use.
 */
export const ASR_CHECK_STORY_IDS = Object.freeze({
  EN_US: 'bf20b9de-0d29-44cf-91a2-c90f2e517894',
  ES_MX: '8534e739-36ff-48b1-9473-ea99dda4df03',
});

// Retired check stories whose rows still exist in SRS and must still be excluded
// from tutor history so they are never mistaken for a real story.
const LEGACY_ES_MX_ASR_CHECK_STORY_ID = '3e75cbb1-5f0a-44a4-a81f-4d17758c3a0f'; // "Es hora de leer ahora" (pre GT-44)
const LEGACY_ASR_CHECK_STORY_IDS = Object.freeze([LEGACY_ES_MX_ASR_CHECK_STORY_ID]);

function toHex(uuid) {
  return String(uuid).replace(/-/g, '').toUpperCase();
}

// Module-private: Object.freeze does not freeze a Set's contents (they live in internal slots),
// so the only real protection is that nothing outside isAsrCheckStoryId can reach it.
const ASR_CHECK_STORY_ID_HEX = new Set(
  [...Object.values(ASR_CHECK_STORY_IDS), ...LEGACY_ASR_CHECK_STORY_IDS].map(toHex),
);
const SPANISH_ASR_CHECK_STORY_ID_HEX = new Set(
  [ASR_CHECK_STORY_IDS.ES_MX, LEGACY_ES_MX_ASR_CHECK_STORY_ID].map(toHex),
);

/**
 * True when `storyId` (UUID or SRS hex, any case) is a current or retired ASR-check story.
 * Anything unparseable is "not an ASR check", so a row with no storyId is never dropped here.
 */
export function isAsrCheckStoryId(storyId) {
  if (typeof storyId !== 'string' || !storyId) {
    return false;
  }
  return ASR_CHECK_STORY_ID_HEX.has(toHex(storyId));
}

/**
 * True when `storyId` (UUID or SRS hex, any case) is a current or retired Spanish ASR-check
 * story. The check stories carry no ES_MX tag, and getASRStoryId serves the Spanish one to
 * en-us students with tutorInSpanish or the Spanish pilot, so neither the story's tags nor the
 * session locale says the content is Spanish — only the storyId does (GT-413).
 */
export function isSpanishAsrCheckStoryId(storyId) {
  if (typeof storyId !== 'string' || !storyId) {
    return false;
  }
  return SPANISH_ASR_CHECK_STORY_ID_HEX.has(toHex(storyId));
}
